import React, { useState } from 'react';
import { 
    Box, Typography, Grid, Card, CardContent, CardMedia, Button, 
    IconButton, TextField, Chip, Divider, Avatar, InputAdornment, 
    Drawer, Badge, Dialog, DialogTitle, DialogContent, DialogActions,
    ToggleButtonGroup, ToggleButton, Alert, MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import TableBarIcon from '@mui/icons-material/TableBar';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useApp, saveOrdersOfflineCache, safeSetLocalStorage } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../config/api';
import { printElement, BluetoothPrinterManager } from '../utils/printHelper';
import { playBillRequestSound } from '../utils/soundHelper';

export default function CaptainSimView() {
    const { tables, setTables, orders, setOrders, sections, menuItems, showToast, tenantSubdomain, currentUser, fetchFreshTablesAndOrders, getActiveBranchId, generateUniqueOrderId, generateUniqueKOTId } = useApp();
    const { t, currentLanguage } = useLanguage();

    // Mode Toggle: 'mobile' (Responsive Mobile Native View) vs 'frame' (Desktop Phone Frame Simulator)
    const [viewMode, setViewMode] = useState('mobile');

    // Navigation / Order Flow States
    const [selectedTableId, setSelectedTableId] = useState(null);
    const [activeSection, setActiveSection] = useState(() => {
        if (sections && Array.isArray(sections) && sections.length > 0) {
            const sorted = [...sections].sort((a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0));
            return sorted[0].id;
        }
        return 'all';
    });

    React.useEffect(() => {
        if (sections && Array.isArray(sections) && sections.length > 0) {
            const hasMatch = sections.some(s => String(s.id) === String(activeSection));
            if (!hasMatch || activeSection === 'all') {
                const sorted = [...sections].sort((a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0));
                setActiveSection(sorted[0].id);
            }
        }
    }, [sections]);

    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [cart, setCart] = useState([]); // [{ id, name, nameMr, price, quantity, remarks }]
    const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
    const [customRemarksModalItem, setCustomRemarksModalItem] = useState(null);
    const [customRemarkInput, setCustomRemarkInput] = useState('');
    const [kotPreviewOpen, setKotPreviewOpen] = useState(false);
    const [kotDetails, setKotDetails] = useState(null);

    // Table Shift & Cancellation States
    const [shiftModalOpen, setShiftModalOpen] = useState(false);
    const [targetShiftTableId, setTargetShiftTableId] = useState('');

    const handleShiftTable = () => {
        if (!selectedTableId || !targetShiftTableId) return;
        const sourceTable = tables.find(t => t.id === selectedTableId);
        const targetTable = tables.find(t => t.id === targetShiftTableId);

        if (!sourceTable || !targetTable) return;

        let updatedTables = tables.map(t => {
            if (t.id === selectedTableId) {
                return { ...t, status: 'vacant', activeOrderId: null };
            }
            if (t.id === targetShiftTableId) {
                return { ...t, status: 'occupied', activeOrderId: sourceTable.activeOrderId };
            }
            return t;
        });

        let updatedOrders = orders.map(o => {
            if (o.id === sourceTable.activeOrderId) {
                return { ...o, tableId: targetShiftTableId, type: `Table ${targetTable.number}` };
            }
            return o;
        });

        safeSetLocalStorage('react_pos_tables', updatedTables);
        saveOrdersOfflineCache('react_pos_orders', updatedOrders);
        if (typeof setTables === 'function') setTables(updatedTables);
        if (typeof setOrders === 'function') setOrders(updatedOrders);
        window.dispatchEvent(new Event('storage'));

        showToast(`🔀 Table ${sourceTable.number} चा ऑर्डर Table ${targetTable.number} वर ट्रान्सफर केला!`);
        setShiftModalOpen(false);
        setSelectedTableId(null);
    };

    const handleCancelOrder = () => {
        if (!selectedTableId || !activeOrder) return;
        if (!window.confirm(`तुम्हाला नक्की Table ${activeTable?.number} चा ऑर्डर रद्द (Cancel) करायचा आहे का?`)) return;

        let updatedTables = tables.map(t => {
            if (t.id === selectedTableId) {
                return { ...t, status: 'vacant', activeOrderId: null };
            }
            return t;
        });

        let updatedOrders = orders.map(o => {
            if (o.id === activeOrder.id) {
                return { ...o, status: 'cancelled' };
            }
            return o;
        });

        safeSetLocalStorage('react_pos_tables', updatedTables);
        saveOrdersOfflineCache('react_pos_orders', updatedOrders);
        if (typeof setTables === 'function') setTables(updatedTables);
        if (typeof setOrders === 'function') setOrders(updatedOrders);
        window.dispatchEvent(new Event('storage'));

        showToast(`❌ Table ${activeTable?.number} ची ऑर्डर रद्द करण्यात आली आहे.`);
        setSelectedTableId(null);
    };

    const activeTable = tables.find(t => t.id === selectedTableId);
    const activeOrder = activeTable && activeTable.activeOrderId ? orders.find(o => o.id === activeTable.activeOrderId) : null;

    // Table Actions Bottom Sheet State
    const [tableActionsOpen, setTableActionsOpen] = useState(false);
    const [actionTable, setActionTable] = useState(null);

    const handleOpenTableAction = (table) => {
        setActionTable(table);
        if (navigator.onLine && typeof fetchFreshTablesAndOrders === 'function') {
            fetchFreshTablesAndOrders(true).catch(err => console.warn("Captain table sync error:", err));
        }
        if (table.status === 'occupied' || table.status === 'billed') {
            setTableActionsOpen(true);
        } else {
            handleSelectTable(table.id);
        }
    };

    const handleSelectTable = (tableId) => {
        setSelectedTableId(tableId);
        if (navigator.onLine && typeof fetchFreshTablesAndOrders === 'function') {
            fetchFreshTablesAndOrders(true).catch(err => console.warn("Captain table select sync error:", err));
        }
        const table = tables.find(t => t.id === tableId);
        if (table && table.activeOrderId) {
            const orderObj = orders.find(o => o.id === table.activeOrderId);
            if (orderObj) {
                setCart(orderObj.items.map(i => ({ ...i })));
            } else {
                setCart([]);
            }
        } else {
            setCart([]);
        }
    };

    const handleSplitTable = () => {
        if (!actionTable) return;
        const subTableA = { ...actionTable, id: `${actionTable.id}-A`, number: `${actionTable.number}-A`, status: 'occupied' };
        const subTableB = { ...actionTable, id: `${actionTable.id}-B`, number: `${actionTable.number}-B`, status: 'vacant', activeOrderId: null };

        let updatedTables = tables.filter(t => t.id !== actionTable.id).concat([subTableA, subTableB]);
        localStorage.setItem('react_pos_tables', JSON.stringify(updatedTables));
        if (typeof setTables === 'function') setTables(updatedTables);
        window.dispatchEvent(new Event('storage'));

        showToast(`✂️ Table ${actionTable.number} स्प्लिट करून Table ${subTableA.number} व ${subTableB.number} तयार झाले!`);
        setTableActionsOpen(false);
    };

    const handleRequestBill = () => {
        if (!actionTable) return;

        let updatedTables = tables.map(t => {
            if (t.id === actionTable.id) {
                return { 
                    ...t, 
                    status: 'billed', 
                    billRequested: true, 
                    billRequestedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
                };
            }
            return t;
        });

        localStorage.setItem('react_pos_tables', JSON.stringify(updatedTables));
        if (typeof setTables === 'function') setTables(updatedTables);
        window.dispatchEvent(new Event('storage'));
        window.dispatchEvent(new Event('dinexa_local_sync'));

        playBillRequestSound();

        // Dispatch Bill Request to Cloud API for real-time sync with Main POS & Table Floor Plan
        try {
            const currentSubdomain = tenantSubdomain || currentUser?.subdomain || localStorage.getItem('dinexa_active_tenant_subdomain') || localStorage.getItem('dinexa_tenant_subdomain') || 'annapurna';
            const branchId = currentUser?.branch_id || localStorage.getItem('dinexa_active_branch_id') || (typeof getActiveBranchId === 'function' ? getActiveBranchId() : 1);
            const activeOrder = orders.find(o => String(o.table_id || o.tableId) === String(actionTable.id) || String(o.id) === String(actionTable.activeOrderId));
            const orderId = activeOrder ? activeOrder.id : (actionTable.activeOrderId || (typeof generateUniqueOrderId === 'function' ? generateUniqueOrderId(branchId) : `ORD-B${branchId}-${Date.now().toString().slice(-6)}`));
            
            fetch(`${API_BASE_URL}/tenant/orders?tenant=${encodeURIComponent(currentSubdomain)}&branch_id=${branchId}&_t=${Date.now()}`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': currentSubdomain
                },
                body: JSON.stringify({
                    id: orderId,
                    branch_id: branchId,
                    table_id: actionTable.id,
                    table_number: actionTable.number || '',
                    type: `Table ${actionTable.number || ''}`,
                    waiter_name: currentUser?.name || 'Captain Mobile',
                    status: 'billed',
                    bill_requested: true,
                    subtotal: activeOrder ? (activeOrder.subtotal || 0) : 0,
                    tax: activeOrder ? (activeOrder.tax || 0) : 0,
                    total: activeOrder ? (activeOrder.total || 0) : 0,
                    items: activeOrder ? (activeOrder.items || []) : []
                })
            })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    showToast(`📄 Table ${actionTable.number} चा बिल प्रिंट मेसेज काऊंटरकडे पाठवला!`);
                }
            })
            .catch(e => console.error('Bill request cloud sync failed:', e));
        } catch (e) {
            console.error('Bill request error:', e);
        }

        setTableActionsOpen(false);
    };

    // Add / Update item in Cart
    const handleAddToCart = (menuItem, addQty = 1) => {
        setCart(prev => {
            const existing = prev.find(item => item.id === menuItem.id);
            if (existing) {
                return prev.map(item => item.id === menuItem.id ? { ...item, quantity: item.quantity + addQty } : item);
            } else {
                return [...prev, { 
                    id: menuItem.id, 
                    name: menuItem.name, 
                    nameMr: menuItem.name_mr || menuItem.nameMr || menuItem.name,
                    price: Number(menuItem.price), 
                    quantity: addQty, 
                    remarks: '' 
                }];
            }
        });
    };

    // Direct Quantity Typing Handler (Supports typing numbers directly, e.g. 15)
    const handleQuantityChange = (menuItemId, newQtyStr) => {
        const qty = parseInt(newQtyStr, 10);
        if (isNaN(qty) || qty <= 0) {
            // Remove from cart if 0
            setCart(prev => prev.filter(item => item.id !== menuItemId));
        } else {
            setCart(prev => prev.map(item => item.id === menuItemId ? { ...item, quantity: qty } : item));
        }
    };

    // Remove item
    const handleRemoveFromCart = (menuItemId) => {
        setCart(prev => {
            const existing = prev.find(item => item.id === menuItemId);
            if (existing && existing.quantity > 1) {
                return prev.map(item => item.id === menuItemId ? { ...item, quantity: item.quantity - 1 } : item);
            } else {
                return prev.filter(item => item.id !== menuItemId);
            }
        });
    };

    // Remarks / Special Instructions
    const handleUpdateRemarks = (menuItemId, remarks) => {
        setCart(prev => prev.map(item => item.id === menuItemId ? { ...item, remarks } : item));
    };

    // Send or Print KOT
    const handleProcessKOT = async (shouldPrint = false) => {
        if (cart.length === 0 || !selectedTableId) return;

        const table = tables.find(t => t.id === selectedTableId);
        let updatedOrders = [...orders];
        let updatedTables = [...tables];
        let orderId = table?.activeOrderId;

        const enableGst = localStorage.getItem('dinexa_gst_enabled') === 'true' || localStorage.getItem('dinexa_gst_enabled') === '1';
        const orderSubtotal = cart.reduce((sum, item) => sum + (Number(item.price) * item.quantity), 0);
        const orderTax = enableGst ? (orderSubtotal * 0.05) : 0;
        const orderTotal = orderSubtotal + orderTax;

        const activeBranchId = (typeof getActiveBranchId === 'function' ? getActiveBranchId() : null) || currentUser?.branch_id || 1;
        const kotId = typeof generateUniqueKOTId === 'function' ? generateUniqueKOTId(activeBranchId) : `KOT-B${activeBranchId}-${Math.floor(100 + Math.random() * 900)}`;
        const staffName = currentUser?.name || currentUser?.username || 'Captain Staff';

        const localizedItems = cart.map(item => {
            const menuItem = (menuItems || []).find(m => String(m.id) === String(item.id));
            return {
                id: item.id,
                name: item.name,
                name_mr: item.name_mr || item.nameMr || menuItem?.name_mr || item.name,
                name_hi: item.name_hi || item.nameHi || menuItem?.name_hi || item.name,
                quantity: item.quantity,
                price: Number(item.price),
                remarks: item.remarks || ''
            };
        });

        const currentKOT = {
            id: kotId,
            tableId: selectedTableId,
            tableNumber: table?.number || '',
            waiterName: staffName,
            timestamp: new Date().toISOString(),
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            items: localizedItems
        };

        if (orderId) {
            updatedOrders = updatedOrders.map(order => {
                if (order.id === orderId) {
                    const mergedItems = [...order.items];
                    localizedItems.forEach(cartItem => {
                        const existing = mergedItems.find(mi => mi.id === cartItem.id);
                        if (existing) {
                            existing.quantity = cartItem.quantity;
                            existing.remarks = cartItem.remarks;
                        } else {
                            mergedItems.push({ ...cartItem });
                        }
                    });

                    const sub = mergedItems.reduce((sum, item) => sum + (Number(item.price) * item.quantity), 0);
                    const tx = enableGst ? (sub * 0.05) : 0;
                    const tot = sub + tx - (order.discount || 0);

                    return {
                        ...order,
                        items: mergedItems,
                        kots: [...(order.kots || []), currentKOT],
                        subtotal: sub,
                        tax: tx,
                        total: tot,
                        status: 'preparing'
                    };
                }
                return order;
            });
        } else {
            orderId = typeof generateUniqueOrderId === 'function' ? generateUniqueOrderId(activeBranchId) : `ORD-B${activeBranchId}-${Date.now().toString().slice(-6)}`;

            const newOrder = {
                id: orderId,
                tableId: selectedTableId,
                type: `Table ${table?.number || ''}`,
                waiterName: staffName,
                timestamp: new Date().toISOString(),
                status: 'preparing',
                items: localizedItems,
                kots: [currentKOT],
                subtotal: orderSubtotal,
                tax: orderTax,
                discount: 0,
                total: orderTotal
            };

            updatedOrders.push(newOrder);

            updatedTables = updatedTables.map(t => {
                if (t.id === selectedTableId) {
                    return { ...t, status: 'occupied', activeOrderId: orderId };
                }
                return t;
            });
        }

        // Save to localStorage & update React AppContext State immediately
        saveOrdersOfflineCache('react_pos_orders', updatedOrders);
        safeSetLocalStorage('react_pos_tables', updatedTables);
        if (typeof setOrders === 'function') setOrders(updatedOrders);
        if (typeof setTables === 'function') setTables(updatedTables);
        window.dispatchEvent(new Event('storage'));
        window.dispatchEvent(new Event('dinexa_local_sync'));

        // Dispatch Order to Cloud Backend API for cross-device real-time sync
        try {
            const currentSubdomain = tenantSubdomain || currentUser?.subdomain || localStorage.getItem('dinexa_active_tenant_subdomain') || localStorage.getItem('dinexa_tenant_subdomain') || 'annapurna';
            const branchId = (typeof getActiveBranchId === 'function' ? getActiveBranchId() : null) || currentUser?.branch_id || localStorage.getItem('dinexa_active_branch_id') || 1;
            
            fetch(`${API_BASE_URL}/tenant/orders?tenant=${encodeURIComponent(currentSubdomain)}&branch_id=${branchId}&_t=${Date.now()}`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': currentSubdomain
                },
                body: JSON.stringify({
                    id: orderId,
                    branch_id: branchId,
                    table_id: selectedTableId,
                    table_number: table?.number || '',
                    type: `Table ${table?.number || ''}`,
                    waiter_name: staffName,
                    status: 'cooking',
                    subtotal: orderSubtotal,
                    tax: orderTax,
                    total: orderTotal,
                    items: localizedItems.map(item => ({
                        menu_item_id: item.id,
                        name: item.name,
                        name_mr: item.name_mr,
                        name_hi: item.name_hi,
                        quantity: item.quantity,
                        price: Number(item.price),
                        remarks: item.remarks || ''
                    }))
                })
            })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    console.log(`Order ${orderId} synced to cloud`);
                }
            })
            .catch(e => console.error('Cloud sync error:', e));
        } catch (e) {
            console.error('Cloud sync error:', e);
        }

        setKotDetails(currentKOT);
        setSelectedTableId(null);
        setCart([]);
        setCartDrawerOpen(false);

        if (shouldPrint) {
            const connectionType = localStorage.getItem('dinexa_printer_connection_type') || 'system';
            if (connectionType !== 'system') {
                try {
                    await BluetoothPrinterManager.printKOTCanvas(currentKOT, currentLanguage);
                    showToast(currentLanguage === 'mr' ? `🖨️ ${kotId} सेव्ह झाला व प्रिंट झाला!` : `🖨️ ${kotId} Saved & Printed!`);
                } catch (err) {
                    setKotPreviewOpen(true);
                }
            } else {
                setKotPreviewOpen(true);
            }
        } else {
            showToast(currentLanguage === 'mr' ? `✅ ${kotId} किचनकडे पाठवला!` : `✅ ${kotId} Sent to Kitchen!`);
        }
    };

    const getDisplayName = (item) => {
        if (!item) return '';
        if (currentLanguage === 'mr') return item.name_mr || item.nameMr || item.name;
        if (currentLanguage === 'hi') return item.name_hi || item.nameHi || item.name;
        return item.name;
    };

    // Category list dynamically derived from DB menuItems or defaults
    const categories = ['all', ...Array.from(new Set(menuItems.map(item => item.category_name || item.category || 'Veg')))];

    const filteredMenu = menuItems.filter(item => {
        const catMatch = selectedCategory === 'all' || (item.category_name || item.category) === selectedCategory;
        const nameMatch = !searchQuery || 
            item.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
            (item.name_mr && item.name_mr.includes(searchQuery));
        return catMatch && nameMatch;
    });

    const totalCartItems = cart.reduce((sum, item) => sum + item.quantity, 0);
    const totalCartAmount = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    const activeBranchId = currentUser?.branch_id || localStorage.getItem('dinexa_active_branch_id') || 1;

    // Filter and sort tables by Section & display_order (matching POS layout exactly)
    const filteredTables = (tables || [])
        .filter(t => {
            if (t.status === 'split' || t.id === 'counter') return false;
            if (t.branch_id && String(t.branch_id) !== String(activeBranchId)) return false;
            if (!sections || sections.length === 0 || activeSection === 'all') return true;
            return String(t.section_id) === String(activeSection) || t.sectionName === activeSection;
        })
        .map(t => {
            // Auto-heal ghost occupied tables in Captain View
            const order = t.activeOrderId ? (orders || []).find(o => o.id === t.activeOrderId) : null;
            const isGhost = (t.status === 'occupied' || t.status === 'billed') && (
                !order || 
                order.status === 'cancelled' || 
                order.status === 'served' || 
                !order.items || 
                order.items.length === 0
            );
            if (isGhost) {
                return { ...t, status: 'vacant', activeOrderId: null };
            }
            return t;
        })
        .sort((a, b) => {
            const orderA = parseInt(a.display_order) || 0;
            const orderB = parseInt(b.display_order) || 0;
            if (orderA !== orderB) return orderA - orderB;
            const numA = parseInt(String(a.number).replace(/[^0-9]/g, '')) || 0;
            const numB = parseInt(String(b.number).replace(/[^0-9]/g, '')) || 0;
            return numA - numB;
        });

    // Mobile App UI Core View
    const renderMobileContent = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#f8fafc', overflow: 'hidden' }}>
            {/* Top Bar / App Header */}
            <Box sx={{ 
                p: 1.5, 
                bgcolor: '#0f172a', 
                color: '#ffffff', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {selectedTableId && (
                        <IconButton size="small" onClick={() => setSelectedTableId(null)} sx={{ color: '#ffffff', bgcolor: 'rgba(255,255,255,0.1)' }}>
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    )}
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.1, color: '#ffffff' }}>
                            {selectedTableId ? `Table ${activeTable?.number}` : 'DineXa Waiter'}
                        </Typography>

                    </Box>
                </Box>

                {!selectedTableId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            size="small"
                            onClick={async () => {
                                if (typeof fetchFreshTablesAndOrders === 'function') {
                                    await fetchFreshTablesAndOrders(false);
                                }
                            }}
                            sx={{ color: '#ffffff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                        >
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Box>
                )}

                {selectedTableId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        {activeTable?.status === 'occupied' && (
                            <>
                                <Chip
                                    label="🔀 Shift"
                                    size="small"
                                    onClick={() => setShiftModalOpen(true)}
                                    sx={{ bgcolor: '#ffffff', color: '#0f172a', fontWeight: 900, cursor: 'pointer', height: 26, fontSize: '0.75rem' }}
                                />
                                <Chip
                                    label="❌ Cancel"
                                    size="small"
                                    onClick={handleCancelOrder}
                                    sx={{ bgcolor: '#ef4444', color: '#ffffff', fontWeight: 900, cursor: 'pointer', height: 26, fontSize: '0.75rem' }}
                                />
                            </>
                        )}
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => setCartDrawerOpen(true)}
                            startIcon={
                                <Badge badgeContent={totalCartItems} color="error">
                                    <ShoppingCartIcon fontSize="small" />
                                </Badge>
                            }
                            sx={{ bgcolor: '#008751', fontWeight: 900, borderRadius: 2, '&:hover': { bgcolor: '#006f42' } }}
                        >
                            ₹{totalCartAmount}
                        </Button>
                    </Box>
                )}
            </Box>

            {/* SCREEN 1: Table Selector Grid */}
            {!selectedTableId ? (
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
                    {/* Section Filter Pills */}
                    {sections && sections.length > 0 ? (() => {
                        const sortedSections = [...sections].sort((a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0));
                        return (
                            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1.5, mb: 1 }}>
                                {sortedSections.map(sec => (
                                    <Chip 
                                        key={sec.id}
                                        label={sec.name} 
                                        size="medium"
                                        onClick={() => setActiveSection(sec.id)}
                                        color={String(activeSection) === String(sec.id) ? 'primary' : 'default'}
                                        sx={{ 
                                            fontWeight: 900, 
                                            borderRadius: 2,
                                            px: 1,
                                            bgcolor: String(activeSection) === String(sec.id) ? '#008751' : '#ffffff',
                                            color: String(activeSection) === String(sec.id) ? '#ffffff' : '#334155',
                                            border: '1px solid',
                                            borderColor: String(activeSection) === String(sec.id) ? '#008751' : '#cbd5e1'
                                        }}
                                    />
                                ))}
                            </Box>
                        );
                    })() : null}

                    {/* Table Cards Grid */}
                    <Box sx={{ 
                        flexGrow: 1, 
                        overflowY: 'auto', 
                        display: 'grid', 
                        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, 
                        gap: 1.5
                    }}>
                        {filteredTables.map(table => {
                            const isOccupied = table.status === 'occupied';
                            const isBilled = table.status === 'billed';

                            // Find running order for this table if occupied
                            const tableOrder = table.activeOrderId ? orders.find(o => o.id === table.activeOrderId) : null;
                            const runningTotal = tableOrder ? (tableOrder.total || tableOrder.subtotal || 0) : 0;
                            const runningItemsCount = tableOrder && tableOrder.items ? tableOrder.items.reduce((s, i) => s + (i.quantity || 1), 0) : 0;

                            // High Contrast Status Colors & Badges
                            const statusColor = isOccupied ? '#dc2626' : (isBilled ? '#0284c7' : '#008751');
                            const statusBg = isOccupied ? '#fef2f2' : (isBilled ? '#f0f9ff' : '#f0fdf4');
                            const statusLabel = isOccupied ? '🔴 OCCUPIED' : (isBilled ? '🔵 BILLED' : '🟢 VACANT');

                            return (
                                <Card 
                                    key={table.id}
                                    onClick={() => handleOpenTableAction(table)}
                                    sx={{
                                        cursor: 'pointer',
                                        borderRadius: '20px',
                                        border: '2.5px solid',
                                        borderColor: statusColor,
                                        bgcolor: statusBg,
                                        boxShadow: isOccupied ? '0 4px 12px rgba(220, 38, 38, 0.15)' : '0 2px 4px rgba(0,0,0,0.04)',
                                        minHeight: 115,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 1.5,
                                        transition: 'all 0.15s ease',
                                        '&:active': { transform: 'scale(0.96)' }
                                    }}
                                >
                                    {/* Table Number */}
                                    <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', lineHeight: 1.1 }}>
                                        {table.number}
                                    </Typography>

                                    {/* Running Bill Amount & Items count if occupied */}
                                    {isOccupied && tableOrder ? (
                                        <Box sx={{ textAlign: 'center', my: 0.5 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#dc2626', lineHeight: 1 }}>
                                                ₹{runningTotal}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.65rem' }}>
                                                {runningItemsCount} Items
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.7rem' }}>
                                            {table.sectionName || 'Dine-in'}
                                        </Typography>
                                    )}

                                    {/* High Contrast Status Pill */}
                                    <Chip 
                                        label={statusLabel}
                                        size="small"
                                        sx={{
                                            fontWeight: 900,
                                            fontSize: '0.65rem',
                                            height: 22,
                                            px: 0.8,
                                            bgcolor: statusColor,
                                            color: '#ffffff',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}
                                    />
                                </Card>
                            );
                        })}
                    </Box>
                </Box>
            ) : (
                /* SCREEN 2: Menu Order Pad */
                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
                    {/* Search & Category Header */}
                    <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="शोधा (Search dish in Marathi / English)..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: '#64748b' }} />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: '20px', bgcolor: '#f8fafc' }
                            }}
                        />

                        {/* Category Pills Bar */}
                        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', mt: 1.5, pb: 0.5 }}>
                            {categories.map(cat => (
                                <Chip
                                    key={cat}
                                    label={cat === 'all' ? 'सर्व (All)' : cat}
                                    size="small"
                                    onClick={() => setSelectedCategory(cat)}
                                    color={selectedCategory === cat ? 'primary' : 'default'}
                                    sx={{ 
                                        fontWeight: 800, 
                                        borderRadius: '20px',
                                        fontSize: '0.75rem',
                                        px: 0.5
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Dish List - 100% Full Width Strips with Fixed Right +ADD button */}
                    <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {filteredMenu.map(item => {
                            const cartItem = cart.find(c => c.id === item.id);
                            const qty = cartItem ? cartItem.quantity : 0;

                            return (
                                <Box 
                                    key={item.id}
                                    sx={{ 
                                        width: '100%',
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        p: 1.2, 
                                        px: 2,
                                        borderRadius: '24px',
                                        border: qty > 0 ? '2px solid #008751' : '1px solid #e2e8f0',
                                        bgcolor: qty > 0 ? '#f0fdf4' : '#ffffff',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                                    }}
                                >
                                    {/* Dish Thumbnail */}
                                    <Box 
                                        component="img"
                                        src={item.image_url || item.image || 'images/default_food.png'}
                                        alt=""
                                        onError={(e) => { e.target.onerror = null; e.target.src = 'images/default_food.png'; }}
                                        sx={{ width: 52, height: 52, minWidth: 52, borderRadius: '50%', objectFit: 'cover', mr: 1.5 }}
                                    />

                                    {/* Dish Title & Price (Middle area) */}
                                    <Box sx={{ flexGrow: 1, minWidth: 0, pr: 1 }}>
                                        <Typography 
                                            variant="subtitle2" 
                                            sx={{ 
                                                fontWeight: 800, 
                                                color: '#0f172a', 
                                                lineHeight: 1.2,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {getDisplayName(item)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 900, color: '#008751', fontSize: '0.9rem' }}>
                                            ₹{item.price}
                                        </Typography>
                                    </Box>

                                    {/* PERMANENT RIGHT-ALIGNED + ADD / STEPPER BUTTON */}
                                    <Box sx={{ flexShrink: 0, ml: 'auto' }}>
                                        {qty === 0 ? (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleAddToCart(item, 1)}
                                                sx={{ 
                                                    fontWeight: 800, 
                                                    borderRadius: '20px', 
                                                    px: 2,
                                                    py: 0.4,
                                                    borderColor: '#008751',
                                                    color: '#008751',
                                                    '&:hover': { bgcolor: '#f0fdf4', borderColor: '#006f42' }
                                                }}
                                            >
                                                + ADD
                                            </Button>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#ffffff', border: '1.5px solid #008751', borderRadius: '20px', p: 0.2, px: 0.5, flexShrink: 0 }}>
                                                <IconButton size="small" onClick={() => handleRemoveFromCart(item.id)} sx={{ color: '#dc2626', p: 0.2 }}>
                                                    <RemoveIcon fontSize="small" />
                                                </IconButton>
                                                
                                                {/* Compact 28px Direct Numeric Input Box */}
                                                <input
                                                    type="number"
                                                    value={qty}
                                                    onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                    style={{ 
                                                        width: '28px',
                                                        textAlign: 'center', 
                                                        fontWeight: 900, 
                                                        fontSize: '0.85rem',
                                                        border: 'none',
                                                        outline: 'none',
                                                        background: 'transparent',
                                                        color: '#0f172a',
                                                        padding: 0
                                                    }}
                                                />

                                                <IconButton size="small" onClick={() => handleAddToCart(item, 1)} sx={{ color: '#008751', p: 0.2 }}>
                                                    <AddIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Bottom Floating Action Bar */}
                    {cart.length > 0 && (
                        <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0', boxShadow: '0 -4px 12px rgba(0,0,0,0.08)', display: 'flex', gap: 1 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={() => handleProcessKOT(true)}
                                startIcon={<LocalPrintshopIcon />}
                                sx={{ 
                                    bgcolor: '#ea580c', 
                                    '&:hover': { bgcolor: '#c2410c' }, 
                                    fontWeight: 900, 
                                    fontSize: '0.9rem',
                                    borderRadius: 3,
                                    py: 1.2
                                }}
                            >
                                🖨️ {currentLanguage === 'mr' ? 'KOT प्रिंट करा' : currentLanguage === 'hi' ? 'KOT प्रिंट करें' : 'Print KOT'}
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={() => handleProcessKOT(false)}
                                startIcon={<SendIcon />}
                                sx={{ 
                                    bgcolor: '#008751', 
                                    '&:hover': { bgcolor: '#006f42' }, 
                                    fontWeight: 900, 
                                    fontSize: '0.9rem',
                                    borderRadius: 3,
                                    py: 1.2
                                }}
                            >
                                ⚡ {currentLanguage === 'mr' ? 'फक्त किचनला' : currentLanguage === 'hi' ? 'केवल किचन' : 'Send KOT'}
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {/* Cart Drawer / Bottom Sheet */}
            <Drawer
                anchor="bottom"
                open={cartDrawerOpen}
                onClose={() => setCartDrawerOpen(false)}
                PaperProps={{ sx: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '80vh' } }}
            >
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 900 }}>
                            Table {activeTable?.number} - Cart Review
                        </Typography>
                        <Chip label={`Total: ₹${totalCartAmount}`} color="primary" sx={{ fontWeight: 900 }} />
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                        {cart.map(item => (
                            <Box key={item.id} sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, mb: 1.5, border: '1px solid #e2e8f0' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                        {item.nameMr || item.name}
                                    </Typography>
                                    
                                    {/* Direct Numeric Input Stepper in Drawer */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 2, p: 0.2, flexShrink: 0 }}>
                                        <IconButton size="small" onClick={() => handleRemoveFromCart(item.id)}>
                                            <RemoveIcon fontSize="small" />
                                        </IconButton>
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                            style={{
                                                width: '28px',
                                                textAlign: 'center',
                                                fontWeight: 900,
                                                fontSize: '0.85rem',
                                                border: 'none',
                                                outline: 'none',
                                                background: 'transparent',
                                                color: '#0f172a',
                                                padding: 0
                                            }}
                                        />
                                        <IconButton size="small" onClick={() => handleAddToCart(item, 1)}>
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>

                                {/* Special Instructions / Remarks TextField */}
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="विशेष सूचना (उदा. कम तिखट, नो कांदा)..."
                                    value={item.remarks || ''}
                                    onChange={(e) => handleUpdateRemarks(item.id, e.target.value)}
                                    sx={{ mt: 1, '& input': { py: 0.5, fontSize: '0.8rem' } }}
                                />
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={() => handleProcessKOT(true)}
                            startIcon={<LocalPrintshopIcon />}
                            sx={{ 
                                bgcolor: '#ea580c', 
                                color: '#ffffff',
                                fontWeight: 900, 
                                py: 1.3, 
                                borderRadius: 3,
                                fontSize: '0.95rem',
                                '&:hover': { bgcolor: '#c2410c' } 
                            }}
                        >
                            🖨️ {currentLanguage === 'mr' ? 'सेव्ह करा व KOT प्रिंट करा' : currentLanguage === 'hi' ? 'सेव करें और KOT प्रिंट करें' : 'Save & Print KOT'}
                        </Button>

                        <Button
                            fullWidth
                            variant="outlined"
                            size="large"
                            onClick={() => handleProcessKOT(false)}
                            startIcon={<SendIcon />}
                            sx={{ 
                                borderColor: '#008751', 
                                color: '#008751',
                                fontWeight: 900, 
                                py: 1.1, 
                                borderRadius: 3,
                                fontSize: '0.9rem',
                                '&:hover': { bgcolor: '#f0fdf4', borderColor: '#006f42' } 
                            }}
                        >
                            ⚡ {currentLanguage === 'mr' ? 'फक्त किचनला KOT पाठवा' : currentLanguage === 'hi' ? 'केवल किचन को भेजें' : 'Send KOT to Kitchen Only'}
                        </Button>
                    </Box>
                </Box>
            </Drawer>
            {/* Shift Table Modal Dialog */}
            <Dialog 
                open={shiftModalOpen} 
                onClose={() => setShiftModalOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, color: '#0f172a' }}>
                    🔀 Table Shift (टेबल बदला)
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                        Table <strong>{activeTable?.number}</strong> चा चालू ऑर्डर दुसऱ्या रिकाम्या टेबलावर ट्रान्सफर करा:
                    </Typography>

                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="नवीन रिकामा टेबल निवडा"
                        value={targetShiftTableId}
                        onChange={(e) => setTargetShiftTableId(e.target.value)}
                    >
                        {tables.filter(t => t.status === 'vacant' && t.id !== selectedTableId).map(t => (
                            <MenuItem key={t.id} value={t.id}>
                                Table {t.number} ({t.sectionName || 'Dine-in'})
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setShiftModalOpen(false)} sx={{ color: '#64748b', fontWeight: 700 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!targetShiftTableId}
                        onClick={handleShiftTable}
                        sx={{ bgcolor: '#008751', fontWeight: 900, '&:hover': { bgcolor: '#006f42' } }}
                    >
                        🔀 Confirm Shift
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Table Actions Bottom Sheet Menu */}
            <Drawer
                anchor="bottom"
                open={tableActionsOpen}
                onClose={() => setTableActionsOpen(false)}
                PaperProps={{ sx: { borderTopLeftRadius: 24, borderTopRightRadius: 24, p: 2.5 } }}
            >
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a' }}>
                        Table {actionTable?.number} Actions
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                        {actionTable?.status === 'occupied' ? '🔴 OCCUPIED - चालू ऑर्डर पर्याय निवडा:' : '🔵 BILLED - बिल झालेले टेबल'}
                    </Typography>
                </Box>

                <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={() => {
                                setTableActionsOpen(false);
                                handleSelectTable(actionTable?.id);
                            }}
                            startIcon={<RestaurantMenuIcon />}
                            sx={{ bgcolor: '#008751', fontWeight: 900, py: 1.2, borderRadius: 3, '&:hover': { bgcolor: '#006f42' } }}
                        >
                            📝 Take Order / Add Items (नवीन ऑर्डर जोडा)
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="outlined"
                            size="medium"
                            onClick={() => {
                                setTableActionsOpen(false);
                                setSelectedTableId(actionTable?.id);
                                setShiftModalOpen(true);
                            }}
                            sx={{ fontWeight: 900, borderColor: '#0f172a', color: '#0f172a', py: 1, borderRadius: 3 }}
                        >
                            🔀 Shift Table
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="outlined"
                            size="medium"
                            onClick={handleSplitTable}
                            sx={{ fontWeight: 900, borderColor: '#6366f1', color: '#6366f1', py: 1, borderRadius: 3 }}
                        >
                            ✂️ Split Table
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="outlined"
                            size="medium"
                            onClick={handleRequestBill}
                            sx={{ fontWeight: 900, borderColor: '#0284c7', color: '#0284c7', py: 1, borderRadius: 3 }}
                        >
                            📄 Request Bill
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="medium"
                            color="error"
                            onClick={() => {
                                setTableActionsOpen(false);
                                setSelectedTableId(actionTable?.id);
                                handleCancelOrder();
                            }}
                            sx={{ fontWeight: 900, py: 1, borderRadius: 3 }}
                        >
                            ❌ Cancel Order
                        </Button>
                    </Grid>
                </Grid>
            </Drawer>
        </Box>
    );

    return (
        <Box sx={{ minHeight: 'calc(100vh - 70px)', bgcolor: '#f1f5f9', p: { xs: 0, md: 3 } }}>
            {/* View Mode Selector Header (Available on Desktop to test both full screen & phone simulator frame) */}
            <Box sx={{ 
                display: { xs: 'none', md: 'flex' }, 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 2, 
                px: 2, 
                py: 1, 
                bgcolor: '#ffffff', 
                borderRadius: 3, 
                border: '1px solid #e2e8f0' 
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneAndroidIcon sx={{ color: '#008751' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                        Captain App
                    </Typography>
                </Box>

                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, val) => val && setViewMode(val)}
                    size="small"
                >
                    <ToggleButton value="mobile" sx={{ fontWeight: 800 }}>
                        <PhoneAndroidIcon fontSize="small" sx={{ mr: 0.5 }} /> Full Mobile Screen
                    </ToggleButton>
                    <ToggleButton value="frame" sx={{ fontWeight: 800 }}>
                        <DesktopWindowsIcon fontSize="small" sx={{ mr: 0.5 }} /> Phone Frame Simulator
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Render Mode 1: Full Mobile Native Screen View */}
            {viewMode === 'mobile' ? (
                <Box sx={{ 
                    maxWidth: 500, 
                    mx: 'auto', 
                    height: { xs: 'calc(100vh - 70px)', md: 720 }, 
                    borderRadius: { xs: 0, md: 4 }, 
                    overflow: 'hidden',
                    boxShadow: { xs: 'none', md: '0 20px 25px -5px rgba(0,0,0,0.1)' },
                    border: { xs: 'none', md: '1px solid #cbd5e1' }
                }}>
                    {renderMobileContent()}
                </Box>
            ) : (
                /* Render Mode 2: Phone Frame Mockup Simulator */
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <Box sx={{ 
                        width: 360, 
                        height: 720, 
                        bgcolor: '#0f172a', 
                        borderRadius: 6, 
                        border: '12px solid #1e293b',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        {renderMobileContent()}
                    </Box>
                </Box>
            )}
            {/* KOT Print Preview Dialog */}
            <Dialog 
                open={kotPreviewOpen} 
                onClose={() => setKotPreviewOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>🖨️ {currentLanguage === 'mr' ? 'KOT प्रिंट पावती' : currentLanguage === 'hi' ? 'KOT प्रिंट पर्ची' : 'KOT Print Slip'}</span>
                    <IconButton size="small" onClick={() => setKotPreviewOpen(false)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    {kotDetails && (
                        <Box 
                            id="captain-kot-paper" 
                            sx={{ 
                                bgcolor: '#ffffff', 
                                p: 2, 
                                border: '1px solid #e2e8f0', 
                                borderRadius: 2, 
                                fontFamily: 'monospace',
                                color: '#000000'
                            }}
                        >
                            <Box sx={{ textAlign: 'center', mb: 1 }}>
                                <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', textTransform: 'uppercase', color: '#dc2626' }}>
                                    {currentLanguage === 'mr' ? 'किचन ऑर्डर तिकीट (KOT)' : currentLanguage === 'hi' ? 'किचन ऑर्डर टिकट (KOT)' : 'KITCHEN ORDER TICKET'}
                                </Typography>
                                <Typography sx={{ fontWeight: 900, fontSize: '1.25rem', bgcolor: '#000000', color: '#ffffff', py: 0.4, borderRadius: 1, my: 0.8 }}>
                                    {currentLanguage === 'mr' ? 'टेबल' : currentLanguage === 'hi' ? 'टेबल' : 'TABLE'} {kotDetails.tableNumber}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', fontWeight: 800, color: '#334155' }}>
                                    <span>KOT: {kotDetails.id}</span>
                                    <span>{kotDetails.time}</span>
                                </Box>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, textAlign: 'left', color: '#64748b', mt: 0.2 }}>
                                    {currentLanguage === 'mr' ? 'कॅप्टन / वेटर' : currentLanguage === 'hi' ? 'कैप्टन / वेटर' : 'Captain'}: {kotDetails.waiterName}
                                </Typography>
                            </Box>

                            <Box sx={{ borderBottom: '2px solid #dc2626', my: 1 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900, fontSize: '0.85rem', mb: 0.5, color: '#dc2626' }}>
                                <span>{currentLanguage === 'mr' ? 'पदार्थ' : currentLanguage === 'hi' ? 'सामग्री' : 'Item Description'}</span>
                                <span>{currentLanguage === 'mr' ? 'नग' : currentLanguage === 'hi' ? 'मात्रा' : 'Qty'}</span>
                            </Box>
                            <Box sx={{ borderBottom: '1px dashed #94a3b8', mb: 1 }} />

                            {kotDetails.items.map((item, idx) => {
                                const displayName = currentLanguage === 'mr' 
                                    ? (item.name_mr || item.name) 
                                    : currentLanguage === 'hi' 
                                    ? (item.name_hi || item.name) 
                                    : item.name;
                                return (
                                    <Box key={idx} sx={{ py: 0.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', fontWeight: 900 }}>
                                            <span style={{ flexGrow: 1, pr: 1 }}>{displayName}</span>
                                            <span style={{ whiteSpace: 'nowrap' }}>x {item.quantity}</span>
                                        </Box>
                                        {item.remarks && (
                                            <Typography sx={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 800, fontStyle: 'italic', pl: 1, mt: 0.2 }}>
                                                * {currentLanguage === 'mr' ? 'सूचना' : currentLanguage === 'hi' ? 'निर्देश' : 'Instruction'}: {item.remarks}
                                            </Typography>
                                        )}
                                        <Box sx={{ borderBottom: '1px dashed #e2e8f0', mt: 0.5 }} />
                                    </Box>
                                );
                            })}

                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 700 }}>
                                    Dinexa POS Kitchen System
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, display: 'flex', gap: 1 }}>
                    <Button 
                        variant="contained" 
                        fullWidth 
                        startIcon={<LocalPrintshopIcon />}
                        onClick={() => {
                            printElement('captain-kot-paper');
                            setKotPreviewOpen(false);
                        }}
                        sx={{ bgcolor: '#ef4444', fontWeight: 900, py: 1.2, borderRadius: 2, '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        🖨️ {currentLanguage === 'mr' ? 'प्रिंट करा (Print)' : 'Print KOT'}
                    </Button>
                    <Button 
                        variant="outlined" 
                        onClick={() => setKotPreviewOpen(false)}
                        sx={{ fontWeight: 800, borderRadius: 2 }}
                    >
                        {currentLanguage === 'mr' ? 'बंद करा' : 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
